/**
 * quoteSync.js — Sync quote status when job status changes
 *
 * Job→Quote status mapping:
 *   job 'quoted'    → quote 'sent'
 *   job 'confirmed' → quote 'accepted'
 *   job 'void'      → quote 'rejected'
 *
 * Always targets the most recently created non-superseded, non-rejected quote
 * for the given job.
 */

const JOB_TO_QUOTE_STATUS = {
  quoted: 'sent',
  confirmed: 'accepted',
};

// Statuses at or after 'confirmed' in the pipeline = quote should be 'accepted'
const CONFIRMED_AND_LATER = [
  'confirmed', 'shooting', 'post_production', 'partial_delivery',
  'delivered', 'invoiced', 'paid', 'archived',
];

/**
 * Determine what quote status should be based on job status.
 * @param {string} jobStatus
 * @returns {'sent' | 'accepted' | 'rejected' | null}
 */
export function getQuoteStatusForJob(jobStatus) {
  if (jobStatus === 'void') return 'rejected';
  if (CONFIRMED_AND_LATER.includes(jobStatus)) return 'accepted';
  if (jobStatus === 'quoted') return 'sent';
  return null; // intake — no change
}

/**
 * Find the latest active quote for a job from the quotes list.
 * "Active" = not superseded and not rejected.
 * @param {Array} allQuotes - full quotes list from useQuotes()
 * @param {string} jobId
 * @returns {object|null} the latest active quote, or null
 */
export function getLatestQuoteForJob(allQuotes, jobId) {
  if (!jobId || !allQuotes?.length) return null;
  const candidates = allQuotes
    .filter(q => q.jobId === jobId && q.status !== 'superseded' && q.status !== 'rejected')
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  return candidates[0] || null;
}

/**
 * Sync quote status after a job status change.
 * Call this after successfully updating the job status.
 *
 * @param {object} params
 * @param {string} params.jobId - the job that changed
 * @param {string} params.newJobStatus - the job's new status
 * @param {Array}  params.allQuotes - full quotes list from useQuotes()
 * @param {Function} params.updateQuote - mutation function (useUpdateQuote().mutateAsync)
 * @returns {Promise<void>}
 */
export async function syncQuoteToJobStatus({ jobId, newJobStatus, allQuotes, updateQuote }) {
  const targetQuoteStatus = getQuoteStatusForJob(newJobStatus);
  if (!targetQuoteStatus) return; // intake — nothing to sync

  const quote = getLatestQuoteForJob(allQuotes, jobId);
  if (!quote) return; // no active quote for this job

  // Don't downgrade: if quote is already at or past the target, skip
  const QUOTE_ORDER = ['draft', 'sent', 'accepted'];
  const currentIdx = QUOTE_ORDER.indexOf(quote.status);
  const targetIdx = QUOTE_ORDER.indexOf(targetQuoteStatus);

  // rejected is a terminal override (from void) — always apply
  if (targetQuoteStatus === 'rejected') {
    if (quote.status !== 'rejected') {
      await updateQuote({ id: quote.id, status: 'rejected' });
    }
    return;
  }

  // Only advance forward, never downgrade
  if (currentIdx >= targetIdx) return;

  const updates = { id: quote.id, status: targetQuoteStatus };
  if (targetQuoteStatus === 'sent') updates.sentAt = new Date().toISOString();
  if (targetQuoteStatus === 'accepted') updates.acceptedAt = new Date().toISOString();

  await updateQuote(updates);
}
